// Pure display helpers for the production office screens (Module 04 §4).
//
// EXACTNESS (D-041): every quantity on the module-04 wire is a DECIMAL STRING
// (`numeric(14,3)`; the ATP read is module 06's `numeric(18,6)`), never a JSON
// number — so the parse/compare work here happens on a scaled BigInt, restating
// the inventory `lib/measure.ts` approach rather than importing it across the
// area boundary (each area owns its display rules; the scale claims differ).
// `Number` appears only where a value becomes DISPLAY text.
//
// Money is integer minor units + currency (the money convention) and REDACTION IS KEY
// ABSENCE (§7): `costToDateMinor` answers `undefined` — never 0 — when the
// accumulator keys are absent, and callers drop the cell/column entirely.

import type { TFunction } from "i18next";
import { RequestError } from "../../../../lib/request";
// One date/time renderer for the whole app (src/lib/datetime.ts). Re-exported
// so every module-04/05 call site keeps importing it from here unchanged.
export { formatBusinessDate, formatTimestamp } from "../../../../lib/datetime";
import { productionEn } from "../production.i18n";
import type { WorkOrder } from "../hooks/production-queries";
// The §4.12 link's search is inventory's route contract, so its one pattern
// is imported from there rather than restated (the #282 review found three
// copies of it).
import { isWireQuantity } from "../../inventory/lib/adjust";

/** The widest scale this area ever renders: module 06's 6 dp (the ATP read). */
const SCALE = 1_000_000n;

/**
 * MODULE 04's §2 STATUS VOCABULARY → the §3.5 `StatusChip` variant (UX-3).
 *
 * The design system fixes what each colour MEANS, product-wide (spec §3.5):
 * green = confirmed/running/good, amber = attention/setup, red =
 * failed/scrap/destructive, accent = interactive/selected/current, neutral =
 * everything else. This map is the one place module 04 states which of its
 * statuses is which, so the same status can never read green on one screen and
 * grey on another — which is what six hand-rolled `status-chip is-*` class
 * expressions across five files had already started to allow.
 *
 * The two judgement calls, stated rather than left in a class name:
 *  · `released` / `ready` are ACCENT, not green. A released order is the one
 *    the floor is being pointed at — current, not yet good output. Green here
 *    would claim a finished thing.
 *  · `skipped` is RED alongside `cancelled`. A skipped operation is a step of
 *    the routing that did not happen; §3.2 makes the planner state a reason
 *    for exactly that reason, and amber would read as "needs a look" when the
 *    decision has already been taken.
 *
 * An unknown status (a later slice's seventh) falls back to neutral rather
 * than throwing — a status rendered plainly beats a screen that does not
 * render.
 */
export type ProductionChipVariant = "ok" | "warn" | "bad" | "neutral" | "accent";

const STATUS_VARIANT: Readonly<Record<string, ProductionChipVariant>> = {
  // work order (§2.1)
  draft: "neutral",
  released: "accent",
  in_progress: "ok",
  completed: "ok",
  closed: "neutral",
  cancelled: "bad",
  // operation (§2.2)
  queued: "neutral",
  ready: "accent",
  paused: "warn",
  skipped: "bad",
};

export function productionChipVariant(status: string): ProductionChipVariant {
  return STATUS_VARIANT[status] ?? "neutral";
}

/** Parse an exact decimal string into a ×1e6 BigInt; `undefined` for garbage —
 * a malformed quantity degrades to "unknown", never to 0. */
export function parseQty(text: string | null | undefined): bigint | undefined {
  if (text === null || text === undefined) return undefined;
  const match = /^(-?)(\d{1,18})(?:\.(\d{1,6}))?$/u.exec(text.trim());
  if (match === null) return undefined;
  const [, sign, whole, frac] = match;
  if (whole === undefined) return undefined;
  const magnitude = BigInt(whole) * SCALE + BigInt((frac ?? "").padEnd(6, "0"));
  return sign === "-" ? -magnitude : magnitude;
}

/**
 * The locale's own decimal separator ("," for ru) — read off Intl once per
 * locale (review finding, 2026-08-08: the whole part was localized while the
 * fraction kept a hardcoded ".", so the ru office read "49.4" against the ru
 * floor's "49,4" — one physical quantity, two vocabularies). DISPLAY-only: the
 * wire stays the canonical dot, and typed inputs stay wire-lenient
 * (`isValidQtyInput` below; the floor's D-091 rule).
 */
const decimalSeparators = new Map<string, string>();
function decimalSeparator(locale: string | undefined): string {
  const key = locale ?? "";
  const cached = decimalSeparators.get(key);
  if (cached !== undefined) return cached;
  let separator = ".";
  try {
    separator =
      new Intl.NumberFormat(locale).formatToParts(1.1).find((part) => part.type === "decimal")
        ?.value ?? ".";
  } catch {
    // Unknown locale — keep the canonical dot, same degradation as the
    // grouping formatter below.
  }
  decimalSeparators.set(key, separator);
  return separator;
}

/**
 * Human quantity text: locale group separators AND the locale's decimal
 * separator, trailing zeros trimmed to `maxDp` (default 3 — the module's own
 * scale). Unparseable input is echoed verbatim rather than blanked.
 */
export function formatQty(
  text: string | null | undefined,
  options: { locale?: string | undefined; maxDp?: number | undefined } = {},
): string {
  if (text === null || text === undefined) return "";
  const scaled = parseQty(text);
  if (scaled === undefined) return text;
  const maxDp = options.maxDp ?? 3;
  const negative = scaled < 0n;
  const abs = negative ? -scaled : scaled;
  const whole = abs / SCALE;
  const frac = abs % SCALE;
  const divisor = 10n ** BigInt(6 - maxDp);
  const rounded = (frac + divisor / 2n) / divisor;
  const carried = rounded === 10n ** BigInt(maxDp) ? 1n : 0n;
  const fracDigits = carried === 1n ? 0n : rounded;
  const wholeOut = whole + carried;
  let fracText = maxDp > 0 ? fracDigits.toString().padStart(maxDp, "0").replace(/0+$/u, "") : "";
  if (fracText === "0") fracText = "";
  const wholeText = new Intl.NumberFormat(options.locale, { useGrouping: true }).format(wholeOut);
  const sign = negative && (wholeOut !== 0n || fracText.length > 0) ? "-" : "";
  return fracText.length > 0
    ? `${sign}${wholeText}${decimalSeparator(options.locale)}${fracText}`
    : `${sign}${wholeText}`;
}

/** True when the wire text is a non-zero quantity (issued material, variance…). */
export function qtyIsNonZero(text: string | null | undefined): boolean {
  const scaled = parseQty(text);
  return scaled !== undefined && scaled !== 0n;
}

/** The §2 grammar for a request quantity this slice sends: ≤ 3 dp decimal. */
const QTY_3DP = /^\d{1,11}(\.\d{1,3})?$/u;

/** Client-side validity of a typed quantity, before the server re-checks it. */
export function isValidQtyInput(text: string, { positive }: { positive: boolean }): boolean {
  const trimmed = text.trim();
  if (!QTY_3DP.test(trimmed)) return false;
  if (!positive) return true;
  const scaled = parseQty(trimmed);
  return scaled !== undefined && scaled > 0n;
}

/**
 * Integer minor units for display (the repo-wide money rule: the only float is
 * the final divide for presentation). Falls back to `minor currency` when the
 * runtime does not know the currency.
 */
export function formatMinor(minor: number, currency: string, locale?: string): string {
  try {
    const fmt = new Intl.NumberFormat(locale, { style: "currency", currency });
    const digits = fmt.resolvedOptions().maximumFractionDigits ?? 2;
    return fmt.format(minor / Math.pow(10, digits));
  } catch {
    return `${minor} ${currency}`;
  }
}

/**
 * §4 S1's cost-to-date: the sum of the four accumulators, or `undefined` when
 * the keys are ABSENT (redaction is key absence, §7 — an absent key must never
 * be summed as 0). The serializer strips the four as a set, so presence of
 * `cost_material_minor` implies the rest; each is still checked so a partial
 * payload can never render a fabricated total.
 */
export function costToDateMinor(wo: WorkOrder): number | undefined {
  if (
    wo.cost_material_minor === undefined ||
    wo.cost_labor_minor === undefined ||
    wo.cost_overhead_minor === undefined ||
    wo.cost_waste_variance_minor === undefined
  ) {
    return undefined;
  }
  return (
    wo.cost_material_minor +
    wo.cost_labor_minor +
    wo.cost_overhead_minor +
    wo.cost_waste_variance_minor
  );
}

/** The `YYYY-MM-DD` parts of a business date, or `undefined` if it is not one. */
function businessDateParts(
  dateText: string | null | undefined,
): { y: number; m: number; d: number } | undefined {
  if (dateText === null || dateText === undefined) return undefined;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(dateText.trim());
  if (match === null) return undefined;
  const [, y, m, d] = match;
  if (y === undefined || m === undefined || d === undefined) return undefined;
  return { y: Number(y), m: Number(m), d: Number(d) };
}

// `formatBusinessDate` / `formatTimestamp` MOVED to src/lib/datetime.ts and
// re-exported here (see the export line at the top of this file): the office
// had three date formats on one screen because each area rolled its own, so
// there is now one implementation for the whole app. Behaviour is byte-for-byte
// what it was — this is a move, not a rewrite.

/**
 * The §9.5 clamp window, MIRRORED from the kernel's `CLAMP_WINDOW_MS`
 * (packages/domain-kernel/src/production/shift-calendar.ts) — shift
 * attribution accepts a device instant only inside
 * `[recorded_at − 24 h, recorded_at]`.
 */
export const EVENT_CLAMP_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * What the server's clock rules did to an event's instant, DERIVED from the
 * two wire timestamps — the same predicates the apply pipeline runs, restated
 * so the S2 Events tab surfaces them honestly instead of inventing a flag the
 * wire does not carry:
 *
 *  * `stale` — |occurred_at − recorded_at| > 24 h: the event was applied AND
 *    flagged `stale_snapshot` into the review queue (§9.5; the >24h-skew
 *    predicate specifically, `events-apply.service.ts` step 11);
 *  * `clamped` — occurred_at sits outside the kernel's
 *    `[recorded_at − 24 h, recorded_at]` window, so shift attribution used the
 *    clamped instant while the row keeps the wire value verbatim. Every stale
 *    event is clamped; a seconds-fast device clock is clamped without being
 *    flagged, which is why the two are distinct on screen.
 *
 * An event queued offline and drained later (occurred_at < recorded_at, inside
 * the window) is NEITHER — that is the outbox working as designed (§3.4).
 */
export function eventClockStatus(
  occurredIso: string,
  recordedIso: string,
): { clamped: boolean; stale: boolean; skewHours: number } {
  const occurred = Date.parse(occurredIso);
  const recorded = Date.parse(recordedIso);
  if (Number.isNaN(occurred) || Number.isNaN(recorded)) {
    return { clamped: false, stale: false, skewHours: 0 };
  }
  const skewMs = occurred - recorded;
  const stale = Math.abs(skewMs) > EVENT_CLAMP_WINDOW_MS;
  const clamped = skewMs > 0 || skewMs < -EVENT_CLAMP_WINDOW_MS;
  return { clamped, stale, skewHours: Math.round(Math.abs(skewMs) / 3_600_000) };
}

/**
 * HOW LONG A STILL-OPEN INTERVAL HAS BEEN OPEN (S13, the blocked-stations
 * board).
 *
 * `downtime_logs.duration_minutes` is a GENERATED column that is NULL for
 * exactly the rows this board is about — an open row has no `ended_at`, so the
 * database cannot have computed a duration for it. The server therefore has no
 * elapsed number to serve, and inventing one server-side would bake a stale
 * value into a cached response. What it DOES serve is `started_at`, its own
 * timestamp; elapsed is that instant subtracted from now, which is arithmetic
 * over a server fact rather than a number nobody measured (D-121's rule: a
 * number a factory reasons about is RENDERED, never echoed — and never
 * fabricated).
 *
 * THE GRAIN IS HONEST, NOT PRETTY. Under an hour the answer is MINUTES,
 * because "1 h" for a 61-minute stop and for a 119-minute stop are the same
 * word for two different decisions. Above an hour the minutes stay, because a
 * changeover at 1 h 05 and one at 1 h 55 are also different decisions. Past a
 * day the minutes go — a stop measured in days is a maintenance problem, not a
 * dispatch one, and the extra digits would only add noise.
 *
 * A NEGATIVE INTERVAL IS CLAMPED, NOT RENDERED. A browser clock a few seconds
 * behind the server produces a start "in the future"; "-1 min of downtime" is
 * a nonsense a reader has to decode. It floors at zero, which reads as
 * `underMinute` — the same thing a stop opened four seconds ago reads as, and
 * that is the truth in both cases at this grain.
 */
export interface ElapsedParts {
  readonly days: number;
  readonly hours: number;
  readonly minutes: number;
  /** Total whole minutes, floored at 0 — the sort key and the "is it new" test. */
  readonly totalMinutes: number;
}

export function elapsedSince(startedIso: string, now: number): ElapsedParts | undefined {
  const started = Date.parse(startedIso);
  if (Number.isNaN(started)) return undefined;
  const totalMinutes = Math.max(0, Math.floor((now - started) / 60_000));
  return {
    days: Math.floor(totalMinutes / 1440),
    hours: Math.floor((totalMinutes % 1440) / 60),
    minutes: totalMinutes % 60,
    totalMinutes,
  };
}

/** The sentence for an `ElapsedParts`, in the caller's language. */
export function formatElapsed(t: TFunction, parts: ElapsedParts): string {
  if (parts.totalMinutes === 0) return t("production.blocked.elapsed.underMinute");
  if (parts.totalMinutes < 60) {
    return t("production.blocked.elapsed.minutes", { m: parts.minutes });
  }
  if (parts.totalMinutes < 1440) {
    return parts.minutes === 0
      ? t("production.blocked.elapsed.hours", { h: parts.hours })
      : t("production.blocked.elapsed.hoursMinutes", { h: parts.hours, m: parts.minutes });
  }
  return parts.hours === 0
    ? t("production.blocked.elapsed.days", { d: parts.days })
    : t("production.blocked.elapsed.daysHours", { d: parts.days, h: parts.hours });
}

/** Date-compare against the viewer's LOCAL calendar day (dates, never
 * timestamps — a timezone must not flip a promise). */
export function isDatePast(dateText: string | null | undefined, now: Date = new Date()): boolean {
  const parts = businessDateParts(dateText);
  if (parts === undefined) return false;
  const target = Date.UTC(parts.y, parts.m - 1, parts.d);
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return today > target;
}

/** §4 S1: due is painted red only while a promise is BROKEN — `due_date <
 * today` and the order is not closed/cancelled. */
export function isWorkOrderOverdue(
  wo: Pick<WorkOrder, "due_date" | "status">,
  now: Date = new Date(),
): boolean {
  if (wo.status === "closed" || wo.status === "cancelled") return false;
  return isDatePast(wo.due_date, now);
}

/** Resolve a `name_i18n` map for the active language: exact locale → `en` →
 * first value — the waste screens' rule, restated for this area's rows. */
export function localeLabel(
  name: Readonly<Partial<Record<string, string>>>,
  language: string,
): string {
  const base = language.split("-")[0]?.toLowerCase() ?? "en";
  const exact = name[base];
  if (exact !== undefined && exact.length > 0) return exact;
  const en = name["en"];
  if (en !== undefined && en.length > 0) return en;
  for (const value of Object.values(name)) {
    if (value !== undefined && value.length > 0) return value;
  }
  return "";
}

/** The problem codes this dictionary can say something about (spec 04 §5/§9). */
const TRANSLATED_PROBLEM_CODES: ReadonlySet<string> = new Set(Object.keys(productionEn.problem));

/**
 * The sentence for a failed request: a translated problem code first, then the
 * server's own `title` (already human text), then the caller's fallback key. A
 * raw UPPER_SNAKE code must never reach a user.
 */
export function productionErrorMessage(
  t: TFunction,
  error: unknown,
  fallbackKey = "production.detail.error",
): string {
  if (error instanceof RequestError) {
    const code = error.code;
    if (code !== undefined && TRANSLATED_PROBLEM_CODES.has(code)) {
      return t(`production.problem.${code}`, { defaultValue: error.message });
    }
    if (error.message.length > 0) return error.message;
  }
  return t(fallbackKey);
}

/** The `reason` module 09's release gate puts on its order-facility 403 (spec
 * 04 §3.1's release row, D-290 ruling 7): the releaser's scope does not cover
 * the facility the customer order ships from. */
export const ORDER_FACILITY_OUT_OF_SCOPE = "order_facility_out_of_scope";

/**
 * The create/release modal's refusal sentence (spec 04 §4 S3): the mapping
 * above, plus `PERMISSION_DENIED`. `production.problem` deliberately does not
 * carry that code — the work-order detail shows the server's title beside the
 * mapped sentence (`serverDetailOf`) — but in the modal a 403 is a refusal the
 * planner has to act on, so it is always our sentence, in the screen's
 * language: the pegged release's order-facility 403 names who can release the
 * draft, and any other 403 reads as a plain "no permission" rather than the
 * server's English title ("Not permitted").
 */
export function createReleaseErrorMessage(
  t: TFunction,
  error: unknown,
  fallbackKey = "production.detail.error",
): string {
  if (error instanceof RequestError && error.code === "PERMISSION_DENIED") {
    const reason = (error.problem as Record<string, unknown> | undefined)?.["reason"];
    return reason === ORDER_FACILITY_OUT_OF_SCOPE
      ? t("production.createModal.releaseOrderFacilityOutOfScope")
      : t("production.createModal.permissionDenied");
  }
  return productionErrorMessage(t, error, fallbackKey);
}

/** One open stoppage holding a close, as `WO_CLOSE_BLOCKED` lists it. */
export interface CloseBlockingStoppage {
  readonly downtimeLogId: string;
  readonly workCenterId: string | undefined;
  readonly startedAt: string | undefined;
}

/** What still holds a work order's §3.1 close, read off `WO_CLOSE_BLOCKED`. */
export interface CloseBlockers {
  readonly openConflicts: number;
  readonly openDowntime: number;
  readonly pendingHandoffs: number;
  /** The open stoppages, when the refusal lists them (it caps the list, so
   * this can be shorter than `openDowntime`). */
  readonly stoppages: readonly CloseBlockingStoppage[];
}

function countOf(value: unknown): number {
  return typeof value === "number" && Number.isInteger(value) && value > 0 ? value : 0;
}

/**
 * `WO_CLOSE_BLOCKED` READ AS DATA (D-299 owner ruling R10). The close is
 * pressed by finance as often as by an admin, and the refusal's `detail` is a
 * plain-English fallback — so S2 builds its sentences from the problem's
 * structured extensions, in the reader's language, and never shows the
 * server's prose. Every blocker is read, not only the arm that refused: the
 * refusal carries all three counts as `blockers`, and naming all of them at
 * once spares the reader a close-refuse-close loop.
 *
 * Tolerant in both directions, because a refusal can come from a server on
 * either side of a deploy: `blockers` when present, otherwise the refusing
 * arm's own top-level count (`open_conflicts` / `open_downtime` /
 * `pending_defect_handoffs`, the shape before R10); a stoppage entry without
 * a string id is dropped rather than rendered as a blank. `undefined` for any
 * error that is not `WO_CLOSE_BLOCKED`, or one that names no blocker at all —
 * the caller then falls back to the mapped headline alone.
 */
export function closeBlockersOf(error: unknown): CloseBlockers | undefined {
  if (!(error instanceof RequestError) || error.code !== "WO_CLOSE_BLOCKED") return undefined;
  const ext = (error.problem ?? {}) as Record<string, unknown>;
  const nested = ext["blockers"];
  const source =
    typeof nested === "object" && nested !== null ? (nested as Record<string, unknown>) : ext;
  const rawLogs = ext["open_downtime_logs"];
  const stoppages: CloseBlockingStoppage[] = Array.isArray(rawLogs)
    ? rawLogs.flatMap((entry: unknown) => {
        if (typeof entry !== "object" || entry === null) return [];
        const e = entry as Record<string, unknown>;
        const id = e["downtime_log_id"];
        if (typeof id !== "string" || id.length === 0) return [];
        const wc = e["work_center_id"];
        const started = e["started_at"];
        return [
          {
            downtimeLogId: id,
            workCenterId: typeof wc === "string" ? wc : undefined,
            startedAt: typeof started === "string" ? started : undefined,
          },
        ];
      })
    : [];
  const blockers: CloseBlockers = {
    openConflicts: countOf(source["open_conflicts"]),
    // A listed stoppage is at least one open stoppage, whatever the count says.
    openDowntime: Math.max(countOf(source["open_downtime"]), stoppages.length),
    pendingHandoffs: countOf(source["pending_defect_handoffs"]),
    stoppages,
  };
  return blockers.openConflicts + blockers.openDowntime + blockers.pendingHandoffs > 0
    ? blockers
    : undefined;
}

/** The server's verbatim sentence when it says MORE than our mapped one (a
 * `WO_COMPLETE_BLOCKED` title names the unfinished op) — `undefined` when it
 * would just repeat the mapping, and ALWAYS for `WO_CLOSE_BLOCKED`, which S2
 * words from its structured extensions (`closeBlockersOf`, D-299 R10) — its
 * title is English and would sit under a Russian headline. */
export function serverDetailOf(t: TFunction, error: unknown): string | undefined {
  if (!(error instanceof RequestError)) return undefined;
  if (error.code === "WO_CLOSE_BLOCKED") return undefined;
  const mapped = productionErrorMessage(t, error);
  if (error.message.length === 0 || error.message === mapped) return undefined;
  return error.message;
}

/**
 * THE ONE WAY ON A REFUSAL NAMES AS DATA today (D-308; spec 04 §2.17 and §5
 * row 30): the material goes onto the rack through a stock adjustment in
 * Inventory. The server says it as `way_on`, on e30's closed-order refusal of
 * a return and on the §2.17 row of a floor return synced after the close, so
 * each screen words it in its reader's language instead of relaying the
 * English `detail`. A value this build does not know is ignored, and the
 * generic sentence stands.
 */
export const WAY_ON_STOCK_ADJUSTMENT = "inventory_stock_adjustment";

/**
 * The spec 06 §4.12 link for a D-308 way on (D-315): the stock adjustment
 * form, opened on the order's plant with what the sentence is about filled in,
 * booked back out of WORK IN PROGRESS — the production location still carries
 * the material the order took and never gave back, so a "found" gain would
 * put the same copper on the books twice. Rendered only for a holder of
 * `inventory.adjust` (the link component decides).
 *
 * The link NEVER fills in the quantity (the #282 review): a manual adjustment
 * writes no material-issue reversal, so the order keeps showing the same
 * remainder after the leftover is recorded, and a prefilled remainder invited
 * a second storekeeper to credit the same copper again. What the order says is
 * still out rides as `expected_qty`, which the form shows as a hint. A
 * container the material came off rides as `unit_id` / `unit_serial`, and the
 * form follows D-308 round 2 with it.
 */
export interface WayOnAdjustInput {
  readonly workOrderId: string | null;
  readonly facilityId?: string | undefined;
  readonly productId?: string | undefined;
  readonly lotId?: string | null | undefined;
  readonly unitId?: string | null | undefined;
  readonly unitSerial?: string | undefined;
  readonly expectedQty?: string | undefined;
  readonly uom?: string | undefined;
  readonly direction: "increase" | "decrease";
  readonly reason: "found" | "data_correction";
  readonly from: "work_order" | "conflict";
}

export function wayOnAdjustSearch(input: WayOnAdjustInput): {
  facility_id?: string;
  product_id?: string;
  lot_id?: string;
  unit_id?: string;
  unit_serial?: string;
  expected_qty?: string;
  uom?: string;
  direction: "increase" | "decrease";
  counter: "wip";
  reason: "found" | "data_correction";
  work_order_id?: string;
  from: "work_order" | "conflict";
} {
  const expected = input.expectedQty?.replace(/^-/u, "");
  return {
    ...(input.facilityId !== undefined ? { facility_id: input.facilityId } : {}),
    ...(input.productId !== undefined ? { product_id: input.productId } : {}),
    ...(input.lotId !== undefined && input.lotId !== null ? { lot_id: input.lotId } : {}),
    ...(input.unitId !== undefined && input.unitId !== null ? { unit_id: input.unitId } : {}),
    ...(input.unitSerial !== undefined && input.unitSerial.length > 0
      ? { unit_serial: input.unitSerial }
      : {}),
    ...(expected !== undefined && isWireQuantity(expected) ? { expected_qty: expected } : {}),
    ...(input.uom !== undefined && input.uom.length > 0 ? { uom: input.uom } : {}),
    direction: input.direction,
    counter: "wip",
    reason: input.reason,
    ...(input.workOrderId !== null ? { work_order_id: input.workOrderId } : {}),
    from: input.from,
  };
}

/** A problem extension that is a non-empty string, or `undefined`. */
export function problemStringOf(error: unknown, key: string): string | undefined {
  if (!(error instanceof RequestError)) return undefined;
  const value = (error.problem as Record<string, unknown> | undefined)?.[key];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/**
 * e30's refusal of a RETURN on a closed order (D-308): 409
 * `WO_CLOSED_IMMUTABLE` carrying `way_on: inventory_stock_adjustment`. An
 * ISSUE refused on a closed order carries no `way_on` and keeps the generic
 * closed-order sentence, as does a refusal from a server that predates the key.
 */
export function isClosedReturnRefusal(error: unknown): boolean {
  return (
    error instanceof RequestError &&
    error.code === "WO_CLOSED_IMMUTABLE" &&
    problemStringOf(error, "way_on") === WAY_ON_STOCK_ADJUSTMENT
  );
}
